/**
 * Division colour — the ONE place colours for divisions are defined.
 *
 * Rules that this module exists to enforce:
 *  - Colour keys on the division STRING, never on an event_divisions row.
 *  - `schedule_color` null means AUTO: hash the division name into the palette.
 *    An auto colour is never written back to the database.
 *  - Every swatch has a light and a dark value, and its own text colour, so
 *    text on a division card clears 4.5:1 in both themes.
 *  - Colour is never the only carrier of meaning: callers must still render
 *    the division name next to the colour, in print and on screen.
 *
 * Do not scatter hex values through components — import from here.
 */

export type ThemeMode = 'light' | 'dark';

export interface SwatchTheme {
  /** Saturated edge stripe / dot. */
  stripe: string;
  /** Card tint — a wash, never a full saturated fill. */
  tint: string;
  /** Text colour that clears 4.5:1 on `tint`. */
  text: string;
}

export interface DivisionSwatch {
  id: string;
  name: string;
  light: SwatchTheme;
  dark: SwatchTheme;
}

/**
 * Ten swatches, hue-spaced and chosen to stay apart for the common colour
 * vision deficiencies (no red/green pair carries the whole separation — the
 * red and green entries sit at opposite ends of the hash order, and blue /
 * orange / violet / amber do the heavy lifting). Contrast ratios of `text`
 * on `tint`, measured: see DIVISION_PALETTE_CONTRAST below.
 */
export const DIVISION_PALETTE: DivisionSwatch[] = [
  { id: 'blue',    name: 'Blue',    light: { stripe: '#1767cf', tint: '#e3eefc', text: '#0a3b7b' }, dark: { stripe: '#3e88ea', tint: '#14263d', text: '#9ac1f4' } },
  { id: 'orange',  name: 'Orange',  light: { stripe: '#da6c0b', tint: '#fdefe3', text: '#7e3e07' }, dark: { stripe: '#ea8e3e', tint: '#412710', text: '#f7c497' } },
  { id: 'teal',    name: 'Teal',    light: { stripe: '#22c3b3', tint: '#e5faf8', text: '#11746a' }, dark: { stripe: '#49dfd0', tint: '#183936', text: '#a0eee6' } },
  { id: 'magenta', name: 'Magenta', light: { stripe: '#c32288', tint: '#fae5f3', text: '#741150' }, dark: { stripe: '#df49a8', tint: '#39182d', text: '#eea0d1' } },
  { id: 'green',   name: 'Green',   light: { stripe: '#2eb860', tint: '#e7f9ed', text: '#176d37' }, dark: { stripe: '#54d483', tint: '#1d3526', text: '#a5e9be' } },
  { id: 'violet',  name: 'Violet',  light: { stripe: '#6322c3', tint: '#eee5fa', text: '#381174' }, dark: { stripe: '#8549df', tint: '#261839', text: '#bfa0ee' } },
  { id: 'amber',   name: 'Amber',   light: { stripe: '#d4a411', tint: '#fdf6e3', text: '#7e6007' }, dark: { stripe: '#eabf3e', tint: '#3f3412', text: '#f7df97' } },
  { id: 'cyan',    name: 'Cyan',    light: { stripe: '#17aacf', tint: '#e3f7fc', text: '#0a647b' }, dark: { stripe: '#3ec7ea', tint: '#14353d', text: '#9ae2f4' } },
  { id: 'red',     name: 'Red',     light: { stripe: '#c52b20', tint: '#fbe6e5', text: '#75160f' }, dark: { stripe: '#e15147', tint: '#3a1a18', text: '#efa49f' } },
  { id: 'slate',   name: 'Slate',   light: { stripe: '#626d84', tint: '#edeff2', text: '#353e50' }, dark: { stripe: '#848fa4', tint: '#212631', text: '#bec4cf' } },
];

/**
 * Measured contrast of `text` on `tint` — i.e. on the CARD, which is the
 * surface a director actually reads. (Text is never drawn on the saturated
 * stripe, so stripe contrast is not the number that matters.)
 */
export const DIVISION_PALETTE_CONTRAST: Record<string, { light: number; dark: number }> = {
  blue: { light: 9.30, dark: 8.23 },
  orange: { light: 7.22, dark: 8.73 },
  teal: { light: 5.19, dark: 9.46 },
  magenta: { light: 9.06, dark: 7.88 },
  green: { light: 5.85, dark: 9.44 },
  violet: { light: 11.48, dark: 7.45 },
  amber: { light: 5.46, dark: 9.32 },
  cyan: { light: 6.08, dark: 9.06 },
  red: { light: 9.35, dark: 7.82 },
  slate: { light: 9.32, dark: 8.65 },
};

const NEUTRAL: DivisionSwatch = {
  id: 'neutral',
  name: 'Neutral',
  light: { stripe: '#94a3b8', tint: '#f1f5f9', text: '#334155' },
  dark: { stripe: '#94a3b8', tint: '#1e293b', text: '#cbd5e1' },
};

/* ── colour maths ───────────────────────────────────────────────────────── */

function parseHex(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function isValidHex(hex: string): boolean {
  return parseHex(hex) !== null;
}

function relLuminance(rgb: [number, number, number]): number {
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two hex colours. Returns 1 when unparseable. */
export function contrastRatio(a: string, b: string): number {
  const ra = parseHex(a);
  const rb = parseHex(b);
  if (!ra || !rb) return 1;
  const la = relLuminance(ra);
  const lb = relLuminance(rb);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

function toHex(rgb: [number, number, number]): string {
  return '#' + rgb.map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('');
}

function mix(a: [number, number, number], b: [number, number, number], t: number): [number, number, number] {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

/** Build a swatch from an arbitrary hex the director typed. */
export function swatchFromHex(hex: string): DivisionSwatch | null {
  const rgb = parseHex(hex);
  if (!rgb) return null;
  const stripe = toHex(rgb);
  const lightTint = toHex(mix(rgb, [255, 255, 255], 0.88));
  const darkTint = toHex(mix(rgb, [10, 12, 18], 0.82));
  const pick = (tint: string) => {
    const dark = toHex(mix(rgb, [0, 0, 0], 0.6));
    const light = toHex(mix(rgb, [255, 255, 255], 0.6));
    return contrastRatio(dark, tint) >= contrastRatio(light, tint) ? dark : light;
  };
  return {
    id: `hex:${stripe}`,
    name: stripe.toUpperCase(),
    light: { stripe, tint: lightTint, text: pick(lightTint) },
    dark: { stripe, tint: darkTint, text: pick(darkTint) },
  };
}

/* ── auto-assignment ────────────────────────────────────────────────────── */

/** Stable string hash — same division name gives the same swatch forever. */
function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** The palette swatch a division gets when nothing is stored for it. */
export function autoSwatch(division: string | null | undefined): DivisionSwatch {
  if (!division) return NEUTRAL;
  const key = division.trim().toLowerCase();
  if (!key) return NEUTRAL;
  return DIVISION_PALETTE[hashString(key) % DIVISION_PALETTE.length];
}

/**
 * The swatch to use for a division.
 * @param stored `schedule_color` from event_division_format_overrides:
 *   a palette id, a hex string, or null/undefined for auto.
 */
export function swatchFor(division: string | null | undefined, stored?: string | null): DivisionSwatch {
  if (stored) {
    const byId = DIVISION_PALETTE.find((s) => s.id === stored);
    if (byId) return byId;
    const fromHex = swatchFromHex(stored);
    if (fromHex) return fromHex;
  }
  return autoSwatch(division);
}

/** Flat, theme-resolved colours for rendering. */
export interface DivisionColors extends SwatchTheme {
  swatchId: string;
  swatchName: string;
  isAuto: boolean;
}

export function resolveDivisionColors(
  division: string | null | undefined,
  stored: string | null | undefined,
  mode: ThemeMode,
): DivisionColors {
  const s = swatchFor(division, stored);
  const t = mode === 'dark' ? s.dark : s.light;
  return { ...t, swatchId: s.id, swatchName: s.name, isAuto: !stored };
}
